import { useEffect, useState } from "react";
import { getUserStats, getLastSessionRecap, getVocabStats, getNextUnit, startUnit, getHomework, getReplacementStatus, getStreak, resetOnboarding, type LastSessionRecap, type UserRow, type UserStats, type VocabStats, type CurriculumUnit, type HomeworkTask, type ReplacementStatus, type CefrLevel, type StreakInfo } from "../lib/api";
import { HomeworkView } from "./HomeworkView";
import { ListeningView } from "./ListeningView";
import { SkillsPanel } from "./SkillsPanel";
import { RePlacement } from "./RePlacement";
import { ExamView } from "./ExamView";
import { SessionsView } from "./SessionsView";
import { SettingsView } from "./SettingsView";
import { VocabReview } from "./VocabReview";

export interface LessonStartArgs {
  mode: "free" | "targeted" | "drill";
  unit?: CurriculumUnit;
  promptBlock?: string;
}

interface Props {
  user: UserRow;
  teacherName: string;
  onStart: (args: LessonStartArgs) => void;
}

function levelProgress(level: string | null): number {
  const order = ["A1", "A2", "B1", "B2", "C1", "C2"];
  const idx = level ? order.indexOf(level) : -1;
  if (idx < 0) return 0;
  return Math.round(((idx + 1) / order.length) * 100);
}

export function Dashboard({ user, teacherName, onStart }: Props): JSX.Element {
  const [stats, setStats] = useState<UserStats | null>(null);
  const [recap, setRecap] = useState<LastSessionRecap | null>(null);
  const [vocabStats, setVocabStats] = useState<VocabStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [reviewMode, setReviewMode] = useState(false);
  const [nextUnit, setNextUnit] = useState<CurriculumUnit | null>(null);
  const [showLessonPicker, setShowLessonPicker] = useState(false);
  const [starting, setStarting] = useState(false);
  const [homeworkCount, setHomeworkCount] = useState(0);
  const [hwMode, setHwMode] = useState(false);
  const [listenMode, setListenMode] = useState(false);
  const [replacement, setReplacement] = useState<ReplacementStatus | null>(null);
  const [replMode, setReplMode] = useState(false);
  const [replResult, setReplResult] = useState<{ level: CefrLevel; leveledUp: boolean } | null>(null);
  const [examMode, setExamMode] = useState(false);
  const [streak, setStreak] = useState<StreakInfo | null>(null);
  const [historyMode, setHistoryMode] = useState(false);
  const [settingsMode, setSettingsMode] = useState(false);

  function refreshHomework(): void {
    void getHomework().then((t) => setHomeworkCount(t.length));
  }

  function refreshVocabStats(): void {
    void getVocabStats().then((v) => setVocabStats(v));
  }

  useEffect(() => {
    Promise.allSettled([
      getUserStats(),
      getLastSessionRecap(),
      getVocabStats(),
      getNextUnit(),
      getHomework(),
      getReplacementStatus(),
      getStreak(),
    ]).then(([statsRes, recapRes, vocabRes, unitRes, hwRes, replRes, streakRes]) => {
      if (statsRes.status === "fulfilled") setStats(statsRes.value);
      if (recapRes.status === "fulfilled") setRecap(recapRes.value);
      if (vocabRes.status === "fulfilled") setVocabStats(vocabRes.value);
      if (unitRes.status === "fulfilled") setNextUnit(unitRes.value);
      if (hwRes.status === "fulfilled") setHomeworkCount((hwRes.value as HomeworkTask[]).length);
      if (replRes.status === "fulfilled") setReplacement(replRes.value);
      if (streakRes.status === "fulfilled") setStreak(streakRes.value);
    }).finally(() => setLoading(false));
  }, []);

  if (settingsMode) {
    return (
      <div className="h-full overflow-y-auto bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900">
        <div className="max-w-2xl mx-auto px-6 py-8">
          <h1 className="text-xl font-semibold text-white mb-4">Settings</h1>
          <SettingsView user={user} onClose={() => { setSettingsMode(false); window.location.reload(); }} />
        </div>
      </div>
    );
  }

  if (historyMode) {
    return (
      <div className="h-full overflow-y-auto bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900">
        <div className="max-w-2xl mx-auto px-6 py-8 space-y-4">
          <h1 className="text-xl font-semibold text-white">Session history</h1>
          <SessionsView onClose={() => setHistoryMode(false)} teacherName={teacherName} />
        </div>
      </div>
    );
  }

  if (examMode) {
    return (
      <div className="h-full overflow-y-auto bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900">
        <div className="max-w-2xl mx-auto px-6 py-8 space-y-4">
          <h1 className="text-xl font-semibold text-white">IELTS Speaking — mock</h1>
          <ExamView onClose={() => setExamMode(false)} />
        </div>
      </div>
    );
  }

  if (listenMode) {
    return (
      <div className="h-full overflow-y-auto bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900">
        <div className="max-w-2xl mx-auto px-6 py-8 space-y-4">
          <h1 className="text-xl font-semibold text-white">Listening</h1>
          <ListeningView onClose={() => setListenMode(false)} />
        </div>
      </div>
    );
  }

  if (hwMode) {
    return (
      <div className="h-full overflow-y-auto bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900">
        <div className="max-w-xl mx-auto px-6 py-8">
          <h1 className="text-xl font-semibold text-white mb-4">Homework</h1>
          <HomeworkView onClose={() => { setHwMode(false); refreshHomework(); }} />
        </div>
      </div>
    );
  }

  if (reviewMode) {
    return (
      <div className="h-full overflow-y-auto bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900">
        <div className="max-w-xl mx-auto px-6 py-8">
          <button
            onClick={() => {
              setReviewMode(false);
              refreshVocabStats();
            }}
            className="text-xs text-ink-400 hover:text-white mb-4"
          >
            ← Back to dashboard
          </button>
          <VocabReview onDone={() => { setReviewMode(false); refreshVocabStats(); }} />
        </div>
      </div>
    );
  }

  const lvl = user.level ?? "—";
  const pct = levelProgress(user.level);
  const greeting = user.name ? `Hi, ${user.name}` : "Welcome back";

  return (
    <div className="h-full overflow-y-auto bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900">
      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        <header>
          <h1 className="text-2xl font-semibold text-white">{greeting}</h1>
          <p className="text-sm text-ink-400 mt-1">
            Ready for a lesson with {teacherName}?
          </p>
        </header>

        <section className="rounded-2xl border border-ink-700 bg-ink-900/60 p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-ink-400">
                Your level
              </div>
              <div className="flex items-baseline gap-3 mt-1">
                <div className="text-4xl font-semibold text-white">{lvl}</div>
                {streak && streak.current > 0 && (
                  <div
                    className={`flex items-baseline gap-1 rounded-full px-2.5 py-1 border ${streak.at_risk ? "border-amber-500/40 bg-amber-500/10" : "border-orange-500/40 bg-orange-500/10"}`}
                    title={`Best ever: ${streak.best} days`}
                  >
                    <span className="text-base">{"\ud83d\udd25"}</span>
                    <span className="text-sm font-semibold text-white">{streak.current}</span>
                    <span className="text-[10px] text-ink-300 ml-0.5">
                      {streak.at_risk ? "at risk — finish today" : streak.active_today ? "day streak" : "day streak"}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => setShowLessonPicker(true)}
              className="rounded-xl bg-lime-500 hover:bg-lime-400 text-ink-900 font-semibold px-5 py-3 shadow-lg shadow-lime-500/20"
            >
              ▶ Start lesson
            </button>
          </div>
          <div className="mt-4 h-2 rounded-full bg-ink-800 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-lime-500 to-lime-300"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-ink-500 mt-1 uppercase tracking-widest">
            <span>A1</span>
            <span>A2</span>
            <span>B1</span>
            <span>B2</span>
            <span>C1</span>
            <span>C2</span>
          </div>
        </section>

        {replMode && (
          <RePlacement
            currentLevel={(user.level as CefrLevel | null) ?? null}
            onCancel={() => setReplMode(false)}
            onDone={(newLevel, leveledUp) => {
              setReplMode(false);
              setReplResult({ level: newLevel, leveledUp });
              // Refresh
              setTimeout(() => window.location.reload(), 1500);
            }}
          />
        )}

        {replResult && (
          <section className={`rounded-2xl border p-5 text-center ${replResult.leveledUp ? "border-emerald-700/40 bg-emerald-500/10" : "border-ink-700 bg-ink-900/60"}`}>
            <div className="text-3xl mb-1">{replResult.leveledUp ? "🎉" : "✓"}</div>
            <div className="text-white font-semibold">
              {replResult.leveledUp ? `Level up — you're now ${replResult.level}` : `You're still at ${replResult.level}`}
            </div>
            <div className="text-xs text-ink-400 mt-1">Refreshing in a moment…</div>
          </section>
        )}

        {!replMode && !replResult && replacement?.due && (
          <section className="rounded-2xl border border-sky-700/40 bg-sky-500/5 p-4 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-sky-300/80">Monthly check-in</div>
              <div className="text-base text-white mt-0.5">Time for a 5-minute level check.</div>
              <div className="text-[11px] text-ink-500 mt-0.5">
                {replacement.days_since == null ? "First check-in" : `${replacement.days_since} days since last placement`}
              </div>
            </div>
            <button
              onClick={() => setReplMode(true)}
              className="rounded-xl bg-sky-500 hover:bg-sky-400 text-ink-900 font-semibold px-4 py-2.5 text-sm"
            >
              Start check-in
            </button>
          </section>
        )}

        {homeworkCount > 0 && (
          <section className="rounded-2xl border border-amber-700/40 bg-amber-500/5 p-4 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-amber-300/80">Homework</div>
              <div className="text-2xl font-semibold text-white mt-0.5">
                {homeworkCount}
                <span className="text-sm font-normal text-ink-400 ml-2">pending</span>
              </div>
              <div className="text-[11px] text-ink-500 mt-0.5">Assigned by Arthur last lesson</div>
            </div>
            <button
              onClick={() => setHwMode(true)}
              className="rounded-xl bg-amber-500 hover:bg-amber-400 text-ink-900 font-semibold px-4 py-2.5 text-sm"
            >
              Do homework
            </button>
          </section>
        )}

        {vocabStats && vocabStats.due_now > 0 && (
          <section className="rounded-2xl border border-sky-700/40 bg-sky-500/5 p-4 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-sky-300/80">
                Words to review
              </div>
              <div className="text-2xl font-semibold text-white mt-0.5">
                {vocabStats.due_now}
                <span className="text-sm font-normal text-ink-400 ml-2">due now</span>
              </div>
              <div className="text-[11px] text-ink-500 mt-0.5">
                {vocabStats.due_today} today · {vocabStats.total} total
              </div>
            </div>
            <button
              onClick={() => setReviewMode(true)}
              className="rounded-xl bg-sky-500 hover:bg-sky-400 text-ink-900 font-semibold px-4 py-2.5 text-sm"
            >
              Review now
            </button>
          </section>
        )}

        {recap && recap.narrative_summary && (
          <section className="rounded-2xl border border-lime-700/40 bg-lime-500/5 p-5">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xs uppercase tracking-widest text-lime-300/80">
                Last lesson
              </h2>
              <div className="text-[11px] text-ink-400">
                {recap.ended_at
                  ? new Date(recap.ended_at).toLocaleDateString(undefined, {
                      day: "numeric",
                      month: "short",
                    })
                  : ""}
              </div>
            </div>
            <p className="text-sm text-ink-100 leading-relaxed">
              {recap.narrative_summary}
            </p>
            {recap.focus_for_next_time && (
              <div className="mt-3 rounded-lg border border-lime-700/40 bg-lime-500/10 px-3 py-2 text-xs">
                <span className="text-lime-300/80 font-medium">{"Today's focus: "}</span>
                <span className="text-ink-100">{recap.focus_for_next_time}</span>
              </div>
            )}
          </section>
        )}

        <section className="grid grid-cols-3 gap-3">
          <Stat
            label="Sessions"
            value={loading ? "…" : String(stats?.total_sessions ?? 0)}
          />
          <Stat
            label="Your turns"
            value={loading ? "…" : String(stats?.total_turns_user ?? 0)}
          />
          <Stat
            label="Errors logged"
            value={loading ? "…" : String(stats?.total_errors ?? 0)}
          />
        </section>

        <SkillsPanel />

        <section className="rounded-2xl border border-ink-700 bg-ink-900/60 p-5">
          <h2 className="text-xs uppercase tracking-widest text-ink-400 mb-3">
            Things to focus on
          </h2>
          {loading ? (
            <p className="text-sm text-ink-500">Loading…</p>
          ) : stats && stats.top_recurring_errors.length > 0 ? (
            <ul className="space-y-2">
              {stats.top_recurring_errors.map((e, i) => (
                <li
                  key={i}
                  className="rounded-lg border border-ink-700 bg-ink-800/60 px-3 py-2 text-sm"
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-white">
                      {e.category.replace(/-/g, " ")}
                    </div>
                    <div className="font-mono text-[11px] text-ink-400">
                      {e.type} · ×{e.count}
                    </div>
                  </div>
                  <div className="text-xs text-ink-400 mt-1">{e.explanation}</div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-400">
              No data yet — start your first session and we'll begin tracking what to practice.
            </p>
          )}
        </section>

        {showLessonPicker && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink-900/80 backdrop-blur p-4">
            <div className="w-full max-w-md rounded-2xl border border-ink-700 bg-ink-900 p-6 space-y-4 shadow-2xl">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">Choose lesson type</h2>
                <button
                  onClick={() => setShowLessonPicker(false)}
                  className="text-ink-400 hover:text-white text-xl leading-none"
                  aria-label="Close"
                >
                  {"×"}
                </button>
              </div>

              <button
                disabled={starting}
                onClick={() => {
                  setShowLessonPicker(false);
                  onStart({ mode: "free" });
                }}
                className="w-full text-left rounded-xl border border-ink-700 bg-ink-800/60 hover:border-lime-500 px-4 py-3 transition disabled:opacity-50"
              >
                <div className="flex items-center justify-between">
                  <div className="font-medium text-white">{"💬"} Free conversation</div>
                  <div className="text-xs text-ink-400">~15 min</div>
                </div>
                <div className="text-xs text-ink-400 mt-1">
                  Open-ended chat. Arthur picks topics based on your interests + history.
                </div>
              </button>

              <button
                disabled={starting || !nextUnit}
                onClick={async () => {
                  if (!nextUnit) return;
                  setStarting(true);
                  const r = await startUnit(nextUnit.slug, "targeted");
                  setStarting(false);
                  if (r) {
                    setShowLessonPicker(false);
                    onStart({ mode: "targeted", unit: r.unit, promptBlock: r.prompt_block });
                  }
                }}
                className="w-full text-left rounded-xl border border-ink-700 bg-ink-800/60 hover:border-lime-500 px-4 py-3 transition disabled:opacity-50"
              >
                <div className="flex items-center justify-between">
                  <div className="font-medium text-white">{"🎯"} Targeted lesson</div>
                  <div className="text-xs text-ink-400">~{nextUnit?.durationMin ?? 12} min</div>
                </div>
                {nextUnit ? (
                  <div className="text-xs text-ink-300 mt-1">
                    <div className="font-medium">{nextUnit.title}</div>
                    <div className="text-ink-400 mt-0.5">
                      {nextUnit.level} {"·"} {nextUnit.track} {"·"} {nextUnit.grammarFocus.replace(/_/g, " ")}
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-ink-500 mt-1">No more units at your level — pick free conversation or come back after a level-up.</div>
                )}
              </button>

              <button
                disabled={starting || !nextUnit}
                onClick={async () => {
                  if (!nextUnit) return;
                  setStarting(true);
                  const r = await startUnit(nextUnit.slug, "drill");
                  setStarting(false);
                  if (r) {
                    setShowLessonPicker(false);
                    onStart({ mode: "drill", unit: r.unit, promptBlock: r.prompt_block });
                  }
                }}
                className="w-full text-left rounded-xl border border-ink-700 bg-ink-800/60 hover:border-lime-500 px-4 py-3 transition disabled:opacity-50"
              >
                <div className="flex items-center justify-between">
                  <div className="font-medium text-white">{"⚡"} Quick drill</div>
                  <div className="text-xs text-ink-400">~10 min</div>
                </div>
                <div className="text-xs text-ink-400 mt-1">
                  Tight focused practice: prompt → attempt → quick correction → next.
                </div>
              </button>

              <button
                onClick={() => { setShowLessonPicker(false); setListenMode(true); }}
                className="w-full text-left rounded-xl border border-ink-700 bg-ink-800/60 hover:border-lime-500 px-4 py-3 transition"
              >
                <div className="flex items-center justify-between">
                  <div className="font-medium text-white">{"🎧"} Listening</div>
                  <div className="text-xs text-ink-400">~5 min</div>
                </div>
                <div className="text-xs text-ink-400 mt-1">
                  Short audio + comprehension questions. Trains your ear, no mic needed.
                </div>
              </button>

              {user.goal_track === "exam_ielts" && (
                <button
                  onClick={() => { setShowLessonPicker(false); setExamMode(true); }}
                  className="w-full text-left rounded-xl border border-sky-700/40 bg-sky-500/5 hover:border-sky-500 px-4 py-3 transition"
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-white">{"🎓"} IELTS mock test</div>
                    <div className="text-xs text-sky-300">~12 min</div>
                  </div>
                  <div className="text-xs text-ink-400 mt-1">
                    Part 1 + Part 2 cue card + Part 3 discussion. Get a band estimate with feedback.
                  </div>
                </button>
              )}
            </div>
          </div>
        )}

        <section className="text-xs text-ink-500 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <span>Goal: </span>
            <span className="text-ink-300">{user.goal ?? "—"}</span>
            <span className="mx-2">·</span>
            <span>Native: </span>
            <span className="text-ink-300">{user.native_lang}</span>
            <span className="mx-2">·</span>
            <span>Learning: </span>
            <span className="text-ink-300">{user.target_lang}</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setHistoryMode(true)}
              className="text-ink-500 hover:text-ink-300 underline-offset-2 hover:underline"
            >
              Session history
            </button>
            <a
              href="/api/me/report.pdf"
              target="_blank"
              rel="noreferrer"
              className="text-ink-500 hover:text-ink-300 underline-offset-2 hover:underline"
            >
              Download monthly report
            </a>
            <button
              onClick={() => setSettingsMode(true)}
              className="text-ink-500 hover:text-ink-300 underline-offset-2 hover:underline"
            >
              Settings
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-xl border border-ink-700 bg-ink-900/60 px-4 py-3">
      <div className="text-2xl font-semibold text-white">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-ink-400 mt-1">
        {label}
      </div>
    </div>
  );
}
